import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs';
import {
  ApiQuestionConfiguration,
  Question,
} from 'src/app/core/models/questionnaire.model';
import {
  AnswersMap,
  QuestionnaireAnswersStateService,
} from 'src/app/core/services/questionnaire-answers-state.service';
import { QuestionnaireService } from 'src/app/core/services/survey.service';
import { QuestionAnswer } from '../question/question.component';

/** Fire after 5 seconds of input inactivity. */
const INACTIVITY_DEBOUNCE_MS = 5000;

@Component({
  selector: 'app-question-api',
  templateUrl: './question-api.component.html',
  styleUrls: ['./question-api.component.scss'],
})
export class QuestionApiComponent implements OnInit, OnDestroy {
  @Input('question') question!: Question;
  @Input() user: any;
  @Output('changeAnswer') changeAnswer: EventEmitter<QuestionAnswer> =
    new EventEmitter();

  isLoading = false;
  hasError = false;

  private dependentSlugs: string[] = [];
  private trigger$ = new Subject<AnswersMap>();
  private subscriptions: Subscription[] = [];
  private lastInputsKey: string | null = null;

  constructor(
    private surveyService: QuestionnaireService,
    private route: ActivatedRoute,
    @Optional() private answersState: QuestionnaireAnswersStateService | null
  ) {}

  ngOnInit(): void {
    this.dependentSlugs = this.extractDependentSlugs();
    this.setupReactivePipeline();

    const initial = this.answersState?.getSnapshot() ?? {};

    // If inputs are already filled but differ from the stored key in the saved answer,
    // the answer is stale (user changed an upstream question on a previous page).
    // Block Submit until the fresh API result arrives.
    if (this.dependentSlugs.length > 0 && !this.hasInvalidDependency(initial)) {
      const currentKey = this.computeInputsKey(initial);
      const storedKey = (this.question.answer?.addtional_information as any)?._clientInputsKey;
      if (storedKey !== currentKey) {
        this.question.isValid = !!this.question.elective;
        this.question.messageError = '';
      }
    }

    // Fire immediately on page load — bypasses the 5s inactivity debounce.
    // The isLoading guard in executeRequest prevents concurrent overlap with
    // blur or inactivity triggers that may follow.
    this.executeRequest(initial);

    // User-typing changes feed the 5s inactivity pipeline only.
    if (this.answersState && this.dependentSlugs.length > 0) {
      const sub = this.answersState
        .changes$()
        .subscribe((answers) => this.trigger$.next(answers));
      this.subscriptions.push(sub);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.trigger$.complete();
  }

  get apiConfig(): ApiQuestionConfiguration['api'] | null {
    return this.question?.configuration?.api ?? null;
  }

  /** When true, the component renders nothing (only the question's statement is shown by the parent). */
  get isHidden(): boolean {
    const visible = this.apiConfig?.visible;
    return visible !== true;
  }

  /**
   * Walks the activated-route tree (current → parents) looking for a param
   * under any of the given names in both path params and query params.
   */
  private resolveRouteParam(candidates: string[]): string | null {
    let route: ActivatedRoute | null = this.route;
    while (route) {
      const paramMap = route.snapshot.paramMap;
      const queryMap = route.snapshot.queryParamMap;
      for (const key of candidates) {
        const value = paramMap.get(key) ?? queryMap.get(key);
        if (value) return value;
      }
      route = route.parent;
    }
    return null;
  }

  private resolveClaimIdFromRoute(): string | null {
    return this.resolveRouteParam(['cid', 'claimId', 'claim_id']);
  }

  private resolveQuestionnaireId(): string | null {
    // State service is the canonical source — set by QuestionnaireAnswerComponent on load.
    // Route params are the last-resort fallback for edge cases.
    return (
      this.answersState?.getQuestionnaireId() ??
      this.resolveRouteParam(['qcode', 'questionnaireId', 'questionnaire_id', 'sid'])
    );
  }

  /**
   * Parses body and headers, extracting any "answers.{slug}.*" references.
   * Returns the unique list of slugs the question depends on.
   */
  private extractDependentSlugs(): string[] {
    const cfg = this.apiConfig;
    if (!cfg) return [];
    const slugs = new Set<string>();
    const collect = (value: any) => {
      if (typeof value !== 'string') return;
      if (!value.startsWith('answers.')) return;
      const parts = value.split('.');
      if (parts.length >= 2 && parts[1]) slugs.add(parts[1]);
    };
    Object.values(cfg.request?.body ?? {}).forEach(collect);
    Object.values(cfg.request?.headers ?? {}).forEach(collect);
    return Array.from(slugs);
  }

  /**
   * Builds a minimal answers payload containing only the slugs the question depends on.
   */
  private buildPayloadAnswers(snapshot: AnswersMap) {
    const out: { [slug: string]: { value: any; addtional_information?: any } } = {};
    for (const slug of this.dependentSlugs) {
      const a = snapshot[slug];
      if (a) out[slug] = { value: a.value, addtional_information: a.addtional_information };
    }
    return out;
  }

  /**
   * Returns true if any dependent slug fails its trigger rule (or is empty).
   * No rules configured → only blocks on empty value (regex is optional).
   */
  private hasInvalidDependency(snapshot: AnswersMap): boolean {
    if (this.dependentSlugs.length === 0) return false;
    const rules = this.apiConfig?.trigger?.rules ?? {};

    for (const slug of this.dependentSlugs) {
      const v = snapshot[slug]?.value;
      if (v === null || v === undefined || v === '') return true;

      const rule = rules[slug];
      if (!rule) continue;

      const str = String(v);
      if (rule.minLength != null && str.length < rule.minLength) return true;
      if (rule.maxLength != null && str.length > rule.maxLength) return true;
      if (rule.regex) {
        try {
          if (!new RegExp(rule.regex).test(str)) return true;
        } catch {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Stable serialization of the dependent values — used to skip duplicate executions.
   */
  private computeInputsKey(snapshot: AnswersMap): string {
    return this.dependentSlugs
      .sort()
      .map((s) => `${s}=${JSON.stringify(snapshot[s]?.value ?? null)}`)
      .join('&');
  }

  private setupReactivePipeline(): void {
    // Inactivity pipeline: fires 5 seconds after the last change.
    // Key captured at emission time (before debounce) so distinctUntilChanged
    // works on a frozen primitive, not a mutated object reference.
    const inactivitySub = this.trigger$
      .pipe(
        map((snapshot) => ({ snapshot, key: this.computeInputsKey(snapshot) })),
        debounceTime(INACTIVITY_DEBOUNCE_MS),
        distinctUntilChanged((prev, curr) => prev.key === curr.key),
        switchMap(({ snapshot }) => this.executeRequest(snapshot))
      )
      .subscribe();
    this.subscriptions.push(inactivitySub);

    // Blur pipeline: fires immediately when a dependent input loses focus.
    if (this.answersState && this.dependentSlugs.length > 0) {
      const blurSub = this.answersState
        .slugBlurs$(this.dependentSlugs)
        .pipe(
          map(() => {
            const snapshot = this.answersState!.getSnapshot();
            return { snapshot, key: this.computeInputsKey(snapshot) };
          }),
          distinctUntilChanged((prev, curr) => prev.key === curr.key),
          switchMap(({ snapshot }) => this.executeRequest(snapshot))
        )
        .subscribe();
      this.subscriptions.push(blurSub);
    }
  }

  private async executeRequest(snapshot: AnswersMap): Promise<void> {
    // Prevent concurrent calls from blur and inactivity pipelines.
    if (this.isLoading) return;

    if (!this.apiConfig) {
      this.hasError = true;
      this.markValidity();
      return;
    }

    const claimId = this.resolveClaimIdFromRoute();
    const questionnaireId = this.resolveQuestionnaireId();
    if (!claimId || !questionnaireId || !this.question?.id) {
      console.warn('QuestionApi: missing context', {
        claimId,
        questionnaireId,
        questionId: this.question?.id,
      });
      this.hasError = true;
      this.markValidity();
      return;
    }

    if (this.hasInvalidDependency(snapshot)) {
      return;
    }

    const inputsKey = this.computeInputsKey(snapshot);
    if (this.lastInputsKey === inputsKey && this.question.answer?.value != null) {
      return;
    }

    this.isLoading = true;
    try {
      const result = await this.surveyService.answerApiQuestion({
        claim_id: claimId,
        questionnaire_id: questionnaireId,
        question_id: this.question.id,
        answers: this.buildPayloadAnswers(snapshot),
      });

      if (result?.skipped) {
        this.lastInputsKey = inputsKey;
        return;
      }

      this.question.answer = {
        value: result.answer ?? null,
        addtional_information: {
          ...(result.additionalInfo ?? {}),
          // Track the client-side inputs key so the component can detect
          // on re-init whether the saved answer is stale (cross-page scenario).
          _clientInputsKey: inputsKey,
        },
      };
      this.hasError = false;
      this.lastInputsKey = inputsKey;
      this.markValidity();

      this.changeAnswer.next({
        answer: this.question.answer,
        question: this.question,
      });
    } catch (err) {
      console.error('QuestionApi: execution failed', err);
      this.hasError = true;
      this.markValidity();
    } finally {
      this.isLoading = false;
    }
  }

  private markValidity(): void {
    if (this.hasError) {
      this.question.isValid = !!this.question.elective;
      this.question.messageError = this.question.elective
        ? ''
        : 'ApiQuestionExecutionFailed';
      return;
    }
    const hasAnswer =
      this.question.answer?.value !== undefined &&
      this.question.answer?.value !== null;
    this.question.isValid = this.question.elective || hasAnswer;
    this.question.messageError = '';
  }
}
